import { registerSW } from 'virtual:pwa-register';

// Background, no-interrupt PWA updates — see specs/auto-update.md.
//
// The service worker is registered in "prompt" mode: a freshly deployed version downloads in the
// background but is NOT applied automatically (vite-plugin-pwa's old `autoUpdate` reloaded the page
// the instant a new build was found, which could throw the player out mid-round). Instead we stash
// the waiting update and apply it later at a safe moment — see applyPendingUpdate(), called from the
// Menu. All progress lives in localStorage, so the single reload an update needs is seamless there.
//
// Checked ONCE, at startup: registering the worker makes the browser look for a new sw.js, and that
// is the only check we do (decision D9). No polling, no re-check on resume — a session that has been
// open for days simply keeps running the build it started with until it is next launched.
//
// No-op where service workers aren't available (iOS WKWebView has none) and in dev. iOS needs no
// equivalent: firebase.json serves index.html `no-store`, so its cold launch always fetches the
// current build — the launch IS the update.

let applyUpdate: ((reloadPage?: boolean) => Promise<void>) | null = null;
let updateReady = false;

/** Register the service worker and pick up whatever was deployed since last launch. Call once. */
export function initSwUpdate(): void {
  if (import.meta.env.DEV) return;
  if (!('serviceWorker' in navigator)) return;

  applyUpdate = registerSW({
    onNeedRefresh() {
      // A new version has finished downloading and is waiting. Workbox only gets here once every
      // precached asset is in place, so this flag is proof the replacement exists — the "never
      // reload speculatively" rule (spec D2) is satisfied by construction. Don't reload now; wait
      // until the player is on the Menu.
      updateReady = true;
    },
  });
}

/**
 * Apply a pending update if one is waiting. Triggers skipWaiting + a single page reload, so call
 * this ONLY when no round is in progress (the Menu). Returns true if an update was applied (a
 * reload is now imminent), false if there was nothing to do.
 */
export function applyPendingUpdate(): boolean {
  if (updateReady && applyUpdate) {
    updateReady = false;
    void applyUpdate(true);   // skipWaiting → controllerchange → reload with the new version
    return true;
  }
  return false;
}
